/**
 * AdditionalStructures Component (simulation feature)
 *
 * Lets the user opt in to simulating additional loaded structures alongside the
 * primary (multi-protein fragment runs), sharing one fragment list and parameters.
 * Gated behind an "Add additional structures" checkbox so the default
 * single-structure flow is unchanged; the list shows only the non-primary
 * structures (the primary is chosen in the ProteinSelect picker). Renders nothing
 * unless more than one protein is loaded and a primary is chosen.
 *
 * This is simulation-specific UI: it reads the generic selectedProtein/loadedProteins
 * from ProteinContext, but the multi-protein selection state (selectedProteins,
 * useExistingForAdditional) is owned by SimulationForm and passed in as props, so
 * the common protein-selection controls stay free of simulation concepts.
 */
import { useContext, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import { useSelector } from 'BMapsRedux';
import { ProteinContext } from '../../common/ProteinContextProvider';
import { structureLabelsByUri, structureKey, sameStructure } from '../../../model/structureRef';
import { buildAdditionalCandidates } from './additionalCandidates';

export default function AdditionalStructures({
    disabled = false,
    selectedProteins,
    useExistingForAdditional,
    onToggleProtein,
    onSetSelectedProteins,
    onSetUseExisting,
}) {
    const { selectedProtein, loadedProteins } = useContext(ProteinContext);
    // Structures of interest + curated panels live in the projectState target list.
    // Read it directly in the sim feature (not via the generic ProteinContext) so the
    // common protein controls stay simulation-agnostic (the 6cd4aa64 boundary).
    const targetList = useSelector((state) => state.projectState.targetList);

    // Candidate additional targets: other loaded structures, unloaded structures of
    // interest, and curated GroupTargets (panels). Panel members are usually NOT loaded
    // -- they submit via the reuse path (managed source) without a Prepare pass.
    const { loaded, groups, sois } = buildAdditionalCandidates(
        loadedProteins, selectedProtein, targetList,
    );
    const individualCandidates = [...loaded, ...sois];
    const candidateLabels = structureLabelsByUri(individualCandidates);
    const hasTargetCandidates = groups.length > 0 || sois.length > 0;

    // Opt-in gate: reveal the list only when the user asks for it, so multi-protein
    // is an explicit choice. Default open only if additional structures are already
    // selected (e.g. a restored/pre-populated selection).
    const [showAdditional, setShowAdditional] = useState(
        selectedProteins.some((p) => p !== selectedProtein),
    );

    // "Additional structures" only makes sense once a primary is chosen. Without one,
    // nudge the user to pick a primary first -- but only if doing so would leave
    // something to add (another loaded structure, an SoI, or a panel) (UX-NOTES #2).
    if (!selectedProtein) {
        if (!(loadedProteins?.length > 1) && !hasTargetCandidates) return null;
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary', margin: '2px 0 12px 6px' }}>
                {'Select a protein above to configure its simulation, then optionally add '
                + 'more structures to run with the same fragments & settings.'}
            </Typography>
        );
    }
    // Primary chosen: render nothing if there is nothing to add.
    if (individualCandidates.length === 0 && groups.length === 0) return null;

    function handleToggleOptIn(evt) {
        const opted = evt.target.checked;
        setShowAdditional(opted);
        // Opting out clears any additional targets, so a hidden list never
        // silently carries extra structures into the submission.
        if (!opted) {
            onSetSelectedProteins(selectedProtein ? [selectedProtein] : []);
        }
    }

    function isMemberSelected(mc) {
        return selectedProteins.some((p) => sameStructure(p, mc));
    }
    function selectedMemberCount(members) {
        return members.filter(isMemberSelected).length;
    }
    // Select-all / clear-all for a curated panel, keyed by structureKey so unloaded
    // members (not the same object as anything already selected) still match.
    function handleToggleGroup(members) {
        if (selectedMemberCount(members) === members.length) {
            const keys = new Set(members.map(structureKey));
            onSetSelectedProteins(selectedProteins.filter((p) => !keys.has(structureKey(p))));
        } else {
            const have = new Set(selectedProteins.map(structureKey));
            const toAdd = members.filter((m) => !have.has(structureKey(m)));
            onSetSelectedProteins([...selectedProteins, ...toAdd]);
        }
    }

    return (
        <FormGroup style={{ margin: '2px 0 12px 6px' }}>
            <FormControlLabel
                control={(
                    <Checkbox
                        checked={showAdditional}
                        disabled={disabled}
                        onChange={handleToggleOptIn}
                    />
                )}
                label="Add additional structures for simulation (same fragments & settings)"
            />
            {showAdditional && (
                <>
                    {individualCandidates.map((prot) => (
                        <FormControlLabel
                            key={prot.uri}
                            style={{ marginLeft: '20px' }}
                            control={(
                                <Checkbox
                                    checked={selectedProteins.includes(prot)}
                                    disabled={disabled}
                                    onChange={() => onToggleProtein(prot)}
                                />
                            )}
                            label={candidateLabels.get(prot.uri)}
                        />
                    ))}
                    {groups.map((g) => {
                        const count = selectedMemberCount(g.members);
                        return (
                            <FormControlLabel
                                key={g.groupId}
                                style={{ marginLeft: '20px' }}
                                control={(
                                    <Checkbox
                                        checked={count > 0 && count === g.members.length}
                                        indeterminate={count > 0 && count < g.members.length}
                                        disabled={disabled}
                                        onChange={() => handleToggleGroup(g.members)}
                                    />
                                )}
                                label={`${g.displayName} (${g.members.length})`}
                            />
                        );
                    })}
                    <FormControlLabel
                        style={{ marginLeft: '20px' }}
                        control={(
                            <Checkbox
                                checked={useExistingForAdditional}
                                disabled={disabled}
                                onChange={(e) => onSetUseExisting(e.target.checked)}
                            />
                        )}
                        label="Use existing simulation setup if available"
                    />
                </>
            )}
        </FormGroup>
    );
}
